import clsx from 'clsx';
import { useMemo } from 'react';
import { useBrandedColors } from '../wp-blocks/branded-consumer-loan/hooks/use-branded-colors';

type ProgressBarProps = {
    totalSteps: number;
    currentStep: number;
    currentLabel: string;
    nextLabel: string;
    brandColor?: string;
};

export const ProgressBar = ({ totalSteps, currentStep, currentLabel, nextLabel, brandColor }: ProgressBarProps) => {
    const C = 251.2;
    const { useBrandedColorsBool, getStyle } = useBrandedColors({ brandColor });
    const strokeDasharray = `${C}, ${C}`;

    const visualStep = useMemo(() => (currentStep === 4.5 ? 4 : currentStep), [currentStep]);

    const strokeDashOffset = useMemo(() => {
        const progress = (visualStep / totalSteps) * 100;
        return C - (C * progress) / 100;
    }, [C, visualStep, totalSteps]);

    return (
        <div data-component="progress-bar" className="r-flex r-items-center r-gap-4">
            <div
                className="r-h-24 r-w-24"
                role="progressbar"
                aria-valuemin={1}
                aria-valuemax={totalSteps}
                aria-valuenow={visualStep}
                aria-labelledby="progress-bar-label"
            >
                <svg viewBox="0 0 100 100">
                    <path
                        strokeWidth="10"
                        strokeLinecap="round"
                        strokeDasharray={strokeDasharray}
                        className="r-fill-none r-stroke-gray-200"
                        d="M50 10 a 40 40 0 0 1 0 80 a 40 40 0 0 1 0 -80"
                    />
                    <path
                        strokeWidth="10"
                        strokeLinecap="round"
                        strokeDasharray={strokeDasharray}
                        strokeDashoffset={strokeDashOffset}
                        d="M50 10 a 40 40 0 0 1 0 80 a 40 40 0 0 1 0 -80"
                        className={clsx(
                            'r-fill-none r-transition-all r-duration-1000 r-ease-in-out',
                            useBrandedColorsBool ? 'brand-styles' : 'r-stroke-primary'
                        )}
                        style={useBrandedColorsBool ? getStyle() : {}}
                    />
                    <text x="50" y="50" textAnchor="middle" dy="11" fontSize="28" fontWeight="bold">
                        {visualStep}/{totalSteps}
                    </text>
                </svg>
            </div>
            <div className="r-space-y-4" id="progress-bar-label">
                <p className="r-text-2xl r-font-bold">{currentLabel}</p>
                {nextLabel ? <p>Nästa steg: {nextLabel}</p> : null}
            </div>
        </div>
    );
};
